import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IamService } from '@app/core/services/iam.service';
import * as fromRoot from '@app/reducers';
import { Team } from '@app/shared/models/team';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import { catchError, concatMap, map, skipWhile, switchMap, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromAdmin from '../../reducers';
import { TeamsActions } from '../../teams/actions';
import { GuestsActions } from '../actions';


@Injectable()
export class GuestsEffects {

  loadGuests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.LoadGuests),
      skipWhile(state => !state),
      withLatestFrom(this.store$.select(fromAdmin.getAccountTeams)),
      switchMap(async ([action, teams]) => {
        try {
          const hydratedTeams = await Promise.all(teams.map(team => this.iamService.findTeam(team.id).pipe(switchMap(res => of(res.team))).toPromise()));
          const emails = [];
          hydratedTeams.forEach((team: Team) => {
            if (team?.config?.authentication?.rules) {
              emails.push(...team.config.authentication.rules);
            }
          });
          const emailsUnique = [];
          const map = new Map();
          for (const email of emails) {
            if (!map.has(email.value)) {
              map.set(email.value, true);
              emailsUnique.push({
                type: email.type,
                value: email.value
              });
            }
          }
          return GuestsActions.LoadGuestsSuccess({ emails: emailsUnique });
        } catch (error) {
          return GuestsActions.LoadGuestsFailure({ error });
        }
      })
    )
  );

  addEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.AddEmail),
      switchMap(async (action) => {
        const { team } = await this.iamService.findTeam(action.team).toPromise();
        let config = team?.config;
        let authentication = team?.config?.authentication;
        let rules = team?.config?.authentication?.rules;
        if (config) {
          if (authentication) {
            if (rules && rules.length) {
              const emailExists = rules.find(rule => rule.type === Constant.teamConfig.rulesNames.email && rule.value === action.email);
              if (emailExists) {
                return GuestsActions.AddEmailFailure({ error: 'EMAIL_ALREADY_EXISTS' });
              }
              rules = [...rules, { type: Constant.teamConfig.rulesNames.email, value: action.email }];
            } else {
              rules = [{ type: Constant.teamConfig.rulesNames.email, value: action.email }];
            }
            authentication = {
              mode: Constant.teamConfig.authenticationMode.anonymous,
              rules,
            };
          } else {
            authentication = {
              mode: Constant.teamConfig.authenticationMode.anonymous,
              rules: [
                { type: Constant.teamConfig.rulesNames.email, value: action.email }
              ]
            };
          }
          config = {
            ...config,
            authentication,
          };
        } else {
          config = {
            authentication: {
              mode: Constant.teamConfig.authenticationMode.anonymous,
              rules: [
                { type: Constant.teamConfig.rulesNames.email, value: action.email }
              ]
            }
          };
        }
        return GuestsActions.UpdateTeamConfig({ team: action.team, config });
      })
    )
  );

  addEmailPattern$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.AddEmailPattern),
      switchMap(async (action) => {
        const { team } = await this.iamService.findTeam(action.team).toPromise();
        let config = team?.config;
        let authentication = team?.config?.authentication;
        let rules = team?.config?.authentication?.rules;
        if (config) {
          if (authentication) {
            if (rules && rules.length) {
              const emailExists = rules.find(rule => rule.type === Constant.teamConfig.rulesNames.wildcard && rule.value === action.pattern);
              if (emailExists) {
                return GuestsActions.AddEmailPatternFailure({ error: 'PATTERN_ALREADY_EXISTS' });
              }
              rules = [...rules, { type: Constant.teamConfig.rulesNames.wildcard, value: action.pattern }];
            } else {
              rules = [{ type: Constant.teamConfig.rulesNames.wildcard, value: action.pattern }];
            }
            authentication = {
              mode: Constant.teamConfig.authenticationMode.anonymous,
              rules,
            };
          } else {
            authentication = {
              mode: Constant.teamConfig.authenticationMode.anonymous,
              rules: [
                { type: Constant.teamConfig.rulesNames.wildcard, value: action.pattern }
              ]
            };
          }
          config = {
            ...config,
            authentication,
          };
        } else {
          config = {
            authentication: {
              mode: Constant.teamConfig.authenticationMode.anonymous,
              rules: [
                { type: Constant.teamConfig.rulesNames.wildcard, value: action.pattern }
              ]
            }
          };
        }
        return GuestsActions.UpdateTeamConfig({ team: action.team, config });
      })
    )
  );

  updateTeamConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.UpdateTeamConfig),
      switchMap((action) => {
        return this.iamService.findTeam(action.team).pipe(concatMap((res: any) => {
          const { team } = res;
          return this.iamService.updateTeam(team.id, { config: action.config }).pipe(
            concatMap((res: any) => {
              return [
                GuestsActions.UpdateTeamConfigSuccess({ team: res.team }),
                TeamsActions.loadAccountTeams(),
              ];
            }),
            catchError(() =>
              of(GuestsActions.UpdateTeamConfigFailure({ error: 'FAILED_TO_UPDATE_CONFIG' }))
            )
          );
        }));
      })
    )
  );

  loadGuestDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.LoadGuestDetails),
      switchMap(async (action) => {
        const type = Constant.authenticationPatternEmailWithAt.test(action.guest.value) ? Constant.teamConfig.rulesNames.wildcard : Constant.teamConfig.rulesNames.email;
        const { teams } = await this.iamService.listTeams().toPromise();
        const hydratedTeams = await Promise.all(teams.map(team => this.iamService.findTeam(team.id).pipe(switchMap(res => of(res.team))).toPromise()));
        const guestTeams = [];
        hydratedTeams.forEach((team: Team) => {
          if (team?.config?.authentication?.rules) {
            const emailsAuthorized = team.config.authentication.rules;
            if (emailsAuthorized.some(email => email.value === action.guest.value)) {
              guestTeams.push(team);
            }
          }
        });
        if (guestTeams.length) {
          return GuestsActions.LoadGuestDetailsSuccess({ guest: { ...action.guest, type, teams: guestTeams } });
        } else {
          this.router.navigateByUrl('account/admin/guests');
          return GuestsActions.LoadGuestDetailsFailure({ error: 'GUEST_NOT_FOUND' });
        }
      })
    )
  );

  updateGuestDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.UpdateGuestDetails),
      switchMap(async (action) => {
        const { teams } = await this.iamService.listTeams().toPromise();
        const hydratedTeams = await Promise.all(teams.map(team => this.iamService.findTeam(team.id).pipe(switchMap(res => of(res.team))).toPromise()));
        const guestTeams = [];
        // Retrieving guest teams
        hydratedTeams.forEach((team: Team) => {
          if (team?.config?.authentication?.rules) {
            const emailsAuthorized = team.config.authentication.rules;
            if (emailsAuthorized.some(email => email.value === action.guest.oldEmail)) {
              guestTeams.push(team);
            }
          }
        });
        const reqsUpdate = [];
        guestTeams.forEach((team) => {
          const config = team.config;
          // Remove old email
          const emailsWithoutOldValue = config.authentication.rules.filter(email => email.value !== action.guest.oldEmail);
          // Add new email
          const emailsWithNewValue = [...emailsWithoutOldValue, { value: action.guest.email, type: action.guest.type }];
          config.authentication.rules = emailsWithNewValue;
          reqsUpdate.push(this.iamService.updateTeam(team.id, { config }).toPromise());
        });
        await Promise.all(reqsUpdate);
        return GuestsActions.UpdateGuestDetailsSuccess({ guest: { ...action.guest, value: action.guest.email } });
      })
    )
  );

  attachGuestToTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.AttachGuestToTeam),
      switchMap((action) => {
        return this.iamService.findTeam(action.team).pipe(concatMap((res: any) => {
          const { team } = res;
          let config = team?.config;
          let authentication = team?.config?.authentication;
          let rules = team?.config?.authentication?.rules;
          if (config) {
            if (authentication) {
              if (rules && rules.length) {
                const emailExists = rules.find(rule => rule.type === action.guest.type && rule.value === action.guest.value);
                if (emailExists) {
                  return of(GuestsActions.AttachGuestToTeamFailure({ error: 'GUEST_ALREADY_EXISTS' }));
                }
                rules = [...rules, { type: action.guest.type, value: action.guest.value }];
              } else {
                rules = [{ type: action.guest.type, value: action.guest.value }];
              }
              authentication = {
                mode: Constant.teamConfig.authenticationMode.anonymous,
                rules,
              };
            } else {
              authentication = {
                mode: Constant.teamConfig.authenticationMode.anonymous,
                rules: [
                  { type: action.guest.type, value: action.guest.value }
                ]
              };
            }
            config = {
              ...config,
              authentication,
            };
          } else {
            config = {
              authentication: {
                mode: Constant.teamConfig.authenticationMode.anonymous,
                rules: [
                  { type: action.guest.type, value: action.guest.value }
                ]
              }
            };
          }
          return this.iamService.updateTeam(action.team, { config }).pipe(concatMap(resUpdate => {
            return [GuestsActions.AttachGuestToTeamSuccess({ team: resUpdate.team })];
          }));
        })
        );
      }))
  );

  detachGuestFromTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.DetachGuestFromTeam),
      switchMap((action) => {
        return this.iamService.findTeam(action.team).pipe(concatMap(resTeam => {
          const config = resTeam.team.config;
          // Remove email from team
          const emailsWithoutOldValue = config.authentication.rules.filter(email => email.value !== action.guest.value);
          config.authentication.rules = emailsWithoutOldValue;
          return this.iamService.updateTeam(action.team, { config }).pipe(concatMap(resUpdate => {
            return [GuestsActions.DetachGuestFromTeamSuccess({ team: resUpdate.team })];
          }));
        }));
      })
    )
  );

  deleteGuest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuestsActions.DeleteGuest),
      switchMap((action) => {
        return this.iamService.listTeams().pipe(concatMap(res => {
          const { teams } = res;
          const reqs = [];
          teams.forEach(team => {
            reqs.push(this.iamService.findTeam(team.id));
          });
          return forkJoin(reqs).pipe(concatMap((batchTeams) => {
            const guestTeams = [];
            // Retrieving guest teams
            batchTeams.forEach(({ team }) => {
              if (team?.config?.authentication?.rules) {
                const emailsAuthorized = team.config.authentication.rules;
                if (emailsAuthorized.some(email => email.value === action.email)) {
                  guestTeams.push(team);
                }
              }
            });
            const reqsUpdate = [];
            guestTeams.forEach((team) => {
              const config = team.config;
              // Remove old email
              const emailsWithoutOldValue = config.authentication.rules.filter(email => email.value !== action.email);
              config.authentication.rules = emailsWithoutOldValue;
              if (emailsWithoutOldValue.length === 0) {
                config.authentication.mode = Constant.teamConfig.authenticationMode.authentified;
              }
              reqsUpdate.push(this.iamService.updateTeam(team.id, { config }));
            });
            return forkJoin(reqsUpdate).pipe(map(() => {
              return GuestsActions.DeleteGuestSuccess();
            }));
          })
          );
        }));
      })
    )
  );

  constructor(
    private actions$: Actions,
    private iamService: IamService,
    private router: Router,
    private store$: Store<fromRoot.State & fromAdmin.State>
  ) { }

}
